import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import './style.scss';
import { useTranslation } from 'react-i18next';
import Select from '../../../ui/Select';
import TranslatedFieldsRow from '../../../modules/TranslationsFieldsRow';
import Input from '../../../ui/Input';
import FileUploader from '../../../components/MediaFileUploader';
import { MAX_FILE_UPLOAD_SIZE_MB, MAX_IMAGE_UPLOAD_SIZE_MB, ADMIN_TASK_IMAGE_COMPRESSION_SIZE_MB, } from '../../../app.config.vars/stored';
import UploadIcon from '../../../assets/icons/upload.svg';
import LocationTaskFields from './LocationTask/LocationTaskFields';
import TaskAnswersFields from './SelectionTask/TaskAnswersFields';
import { validateAnswers } from './SelectionTask/helpers';
import Tooltip from '../../../ui/Tooltip';
import InfoIcon from '../../../assets/icons/info.svg';
import RichTextEditor from '../../../ui/RichTextEditor';
import InputTaskAnswersFields from './ValidatedInputTask/InputTaskAnswersFields';
import { useAppDispatch } from '../../../store';
import { setIsModalOpen, setModalContent } from '../../../store/slices/global';
import { MODAL_CONTENT } from '../../../app.config.vars/modalContent';
import { getAdminTask } from '../../../api/admin/tasks';
import { generateId } from '../../../helpers';
import { validateAnswersBeforeSubmit, validateInputTaskAnswersBeforeSubmit, } from './validateFormFields';
const FormFields = ({ taskId, taskTypes, handleMediaUploadChange, refs, formState, validateSelect, formErrors, taskAnswersError, setTaskAnswersError, validateInputField, updatedFormFields, setUpdatedFormFields, formType, setSortedTaskAnswers, setTaskUpdated, isFormStateReady, validateAll, }) => {
    const { t, i18n } = useTranslation();
    const dispatch = useAppDispatch();
    const onTitleTranslationsChange = (langCode, value) => {
        formState.setTitleTranslations((prev) => ({ ...prev, [langCode]: value }));
        validateInputField({ fieldName: `title${langCode.toUpperCase()}`, value });
        setUpdatedFormFields((prev) => {
            const newSet = new Set(prev);
            newSet.add(`title${langCode.toUpperCase()}`);
            return newSet;
        });
    };
    const onDescriptionTranslationsChange = (langCode, value) => {
        formState.setDescriptionTranslations((prev) => ({ ...prev, [langCode]: value }));
        validateInputField({ fieldName: `description${langCode.toUpperCase()}`, value });
        setUpdatedFormFields((prev) => {
            const newSet = new Set(prev);
            newSet.add(`description${langCode.toUpperCase()}`);
            return newSet;
        });
    };
    const onTaskTypeChange = async (selectedValue) => {
        if (formType === 'edit') {
            try {
                const task = await getAdminTask(taskId);
                if (selectedValue === 'single-selection' || selectedValue === 'multiple-selection') {
                    let taskAnswers = task.answers.map((answer) => ({
                        uid: answer.uid,
                        translations: answer.content,
                        isCorrect: answer.isCorrect,
                        sort: answer.sort,
                        clientId: generateId('answer'),
                    }));
                    if (taskAnswers.length === 1) {
                        taskAnswers.push({
                            uid: null,
                            translations: { de: '', en: '', cs: '' },
                            isCorrect: false,
                            sort: 2,
                            clientId: generateId('answer'),
                        });
                    }
                    else if (taskAnswers.length === 0) {
                        taskAnswers = [
                            {
                                uid: null,
                                translations: { de: '', en: '', cs: '' },
                                isCorrect: false,
                                sort: 1,
                                clientId: generateId('answer'),
                            },
                            {
                                uid: null,
                                translations: { de: '', en: '', cs: '' },
                                isCorrect: false,
                                sort: 2,
                                clientId: generateId('answer'),
                            },
                        ];
                    }
                    formState.setTaskAnswers(taskAnswers);
                    validateAnswersBeforeSubmit({
                        taskAnswers,
                        selectedTaskType: selectedValue,
                        setTaskAnswersError,
                        validateAll,
                    });
                    validateAnswers({
                        taskType: selectedValue,
                        taskAnswers,
                        setTaskAnswersError,
                    });
                }
                else if (selectedValue === 'validated-text') {
                    let taskAnswers = task.answers.map((answer) => ({
                        uid: answer.uid,
                        translations: answer.content,
                        clientId: generateId('input-answer'),
                    }));
                    if (taskAnswers.length === 0) {
                        taskAnswers = [
                            {
                                uid: null,
                                translations: { de: '', en: '', cs: '' },
                                clientId: generateId('input-answer'),
                            },
                        ];
                    }
                    formState.setInputTaskAnswers(taskAnswers);
                    validateInputTaskAnswersBeforeSubmit({
                        inputTaskAnswers: taskAnswers,
                        validateAll,
                    });
                }
                else {
                    formState.setTaskAnswers([]);
                    formState.setInputTaskAnswers([]);
                    setTaskAnswersError(null);
                }
            }
            catch (error) {
                dispatch(setIsModalOpen(true));
                dispatch(setModalContent(MODAL_CONTENT.somethingWentWrong.key));
            }
        }
        formState.setSelectedTaskType(selectedValue);
        validateSelect('taskType', !!selectedValue);
        setUpdatedFormFields((prev) => {
            const newSet = new Set(prev);
            newSet.add('selectedTaskType');
            return newSet;
        });
    };
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "form-group", children: [_jsx(Input, { ref: refs[`title${i18n.language.toUpperCase()}`], id: `title-${i18n.language}`, value: formState.titleTranslations[i18n.language], name: `title${i18n.language.toLowerCase()}`, label: t('label.task_title'), placeholder: t('placeholder.title'), onChange: (event) => {
                            onTitleTranslationsChange(i18n.language, event.currentTarget.value);
                        }, error: !!formErrors[`title${i18n.language.toUpperCase()}`]?.length, errorMessage: !!formErrors[`title${i18n.language.toUpperCase()}`]?.length
                            ? t(formErrors[`title${i18n.language.toUpperCase()}`][0]
                                .messageTranslationKey, formErrors[`title${i18n.language.toUpperCase()}`][0]
                                .variableToUseInMessage)
                            : undefined }), _jsx("div", { className: "form-translations-group", children: _jsx(TranslatedFieldsRow, { fieldName: 'title', values: formState.titleTranslations, originContent: formState.titleTranslations[i18n.language], onChange: onTitleTranslationsChange, refs: refs, formErrors: formErrors, isDataReady: isFormStateReady }) })] }), _jsx("div", { className: "form-group", children: _jsx(Input, { ref: refs.internalTitle, id: 'internalTitle', value: formState.internalTitle, name: "internalTitle", label: t('label.internal_title'), placeholder: t('placeholder.internal_title'), tooltipContent: t('tooltip.internal_title'), onChange: (event) => {
                        formState.setInternalTitle(event.currentTarget.value);
                        setUpdatedFormFields((prev) => {
                            const newSet = new Set(prev);
                            newSet.add('internalTitle');
                            return newSet;
                        });
                    } }) }), _jsx("div", { className: "form-group", children: taskTypes && (_jsx("div", { className: "form-field-wrapper", children: _jsx(Select, { zIndex: 2, ref: refs.taskType, size: 'large', background: 'admin-white', options: taskTypes.map((type) => ({
                            label: type.title,
                            value: type.uid,
                        })), manageIsSelected: false, onChange: (selectedValue) => onTaskTypeChange(selectedValue), label: t('label.task_type'), placeholder: t('placeholder.select_task_type'), error: !!formErrors.taskType?.length, errorMessage: !!formErrors.taskType?.length
                            ? t(formErrors.taskType[0].messageTranslationKey)
                            : undefined, defaultSelectedOptionValue: formState.selectedTaskType }) })) }), _jsxs("div", { className: "form-group", children: [_jsx(RichTextEditor, { ref: refs[`description${i18n.language.toUpperCase()}`], id: `description-${i18n.language}`, value: formState.descriptionTranslations[i18n.language], label: t('label.task_description'), placeholder: t('placeholder.task_description'), onChange: (value) => {
                            onDescriptionTranslationsChange(i18n.language, value);
                        }, error: !!formErrors[`description${i18n.language.toUpperCase()}`]?.length, errorMessage: !!formErrors[`description${i18n.language.toUpperCase()}`]?.length
                            ? t(formErrors[`description${i18n.language.toUpperCase()}`][0]
                                .messageTranslationKey, formErrors[`description${i18n.language.toUpperCase()}`][0]
                                .variableToUseInMessage)
                            : undefined, isDataReady: isFormStateReady }), _jsx("div", { className: "form-translations-group", children: _jsx(TranslatedFieldsRow, { fieldName: 'description', textarea: true, values: formState.descriptionTranslations, originContent: formState.descriptionTranslations[i18n.language], onChange: onDescriptionTranslationsChange, placeholder: t('placeholder.task_description'), refs: refs, formErrors: formErrors, isDataReady: isFormStateReady }) })] }), _jsx("div", { className: "form-group task-media-upload", children: _jsx(FileUploader, { maxImageSizeMB: MAX_IMAGE_UPLOAD_SIZE_MB, imageCompressionSizeMB: ADMIN_TASK_IMAGE_COMPRESSION_SIZE_MB, error: false, selectedFile: formState.fileReference, label: t('label.task_media'), additionalLabel: '( */.jpg, */.jpeg, */.png, */.webp, */.svg, */.mp4, */.mov, */.mp3, */.wav, */.m4a )', onChange: handleMediaUploadChange, disabled: false, accept: '.jpg,.jpeg,.png,.webp,.svg,.mp4,.mov,.mp3,.wav,.m4a,.3gp,.amr', buttonLabel: t('button.upload'), buttonVariant: 'outlined', buttonIcon: _jsx(UploadIcon, {}), maxFileSizeMB: MAX_FILE_UPLOAD_SIZE_MB }) }), formState.selectedTaskType === 'location' && (_jsx("div", { className: "form-group", children: _jsx(LocationTaskFields, { refs: refs, formState: formState, formErrors: formErrors, validateInputField: validateInputField, setUpdatedFormFields: setUpdatedFormFields, formType: formType }) })), (formState.selectedTaskType === 'single-selection' ||
                formState.selectedTaskType === 'multiple-selection') && (_jsx("div", { className: "form-group", children: _jsx(TaskAnswersFields, { formState: formState, formErrors: formErrors, validateInputField: validateInputField, answersError: taskAnswersError, setAnswersError: setTaskAnswersError, setUpdatedFormFields: setUpdatedFormFields, setSortedAnswers: setSortedTaskAnswers, taskId: taskId, setTaskUpdated: setTaskUpdated }) })), formState.selectedTaskType === 'validated-text' && (_jsx("div", { className: "form-group", children: _jsx(InputTaskAnswersFields, { formState: formState, formErrors: formErrors, validateInputField: validateInputField, updatedFormFields: updatedFormFields, setUpdatedFormFields: setUpdatedFormFields, setTaskUpdated: setTaskUpdated, taskId: taskId }) })), _jsxs("div", { className: "form-group", children: [_jsxs("div", { className: "label", children: [t('label.scoring'), _jsx(Tooltip, { content: t('tooltip.scoring'), position: "top", children: _jsx(InfoIcon, { style: {
                                        width: '16px',
                                        height: '16px',
                                        marginLeft: '4px',
                                        cursor: 'help',
                                        display: 'inline-block',
                                        verticalAlign: 'middle',
                                    } }) })] }), _jsx(Input, { type: 'number', ref: refs.successPoints, id: "successPoints", value: String(formState.successPoints !== null ? formState.successPoints : ''), name: "successPoints", label: t('label.correct'), labelPosition: 'left', labelWidth: '70px', color: 'gray', placeholder: t('placeholder.success_points'), onChange: (event) => {
                            formState.setSuccessPoints(+event.currentTarget.value);
                            validateInputField({ fieldName: 'successPoints', value: event.currentTarget.value });
                            setUpdatedFormFields((prev) => {
                                const newSet = new Set(prev);
                                newSet.add('successPoints');
                                return newSet;
                            });
                        }, error: !!formErrors.successPoints?.length, errorMessage: !!formErrors.successPoints?.length
                            ? t(formErrors.successPoints[0].messageTranslationKey, formErrors.successPoints[0].variableToUseInMessage)
                            : undefined }), _jsx(Input, { type: 'number', ref: refs.failurePoints, id: "failurePoints", value: String(formState.failurePoints !== null ? formState.failurePoints : ''), name: "failurePoints", label: t('label.incorrect'), labelPosition: 'left', labelWidth: '70px', color: 'gray', placeholder: t('placeholder.failure_points'), onChange: (event) => {
                            formState.setFailurePoints(+event.currentTarget.value);
                            validateInputField({ fieldName: 'failurePoints', value: event.currentTarget.value });
                            setUpdatedFormFields((prev) => {
                                const newSet = new Set(prev);
                                newSet.add('failurePoints');
                                return newSet;
                            });
                        }, error: !!formErrors.failurePoints?.length, errorMessage: !!formErrors.failurePoints?.length
                            ? t(formErrors.failurePoints[0].messageTranslationKey, formErrors.failurePoints[0].variableToUseInMessage)
                            : undefined })] })] }));
};
export default FormFields;
